import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";

import PhoneCase from "../../../assets/images/HeroSection/MobileCovers.jpg";
import KeyChains from "../../../assets/images/HeroSection/KeyChains.jpg";
import Frame from "../../../assets/images/HeroSection/PhotoFrame.jpg";
import HangingFrame from "../../../assets/images/HeroSection/HangingFrame.jpg";
import ClockFrame from "../../../assets/images/HeroSection/ClockFrame.jpg";
import Pillows from "../../../assets/images/HeroSection/Pillows.jpg";

// Keyframe animations
const slideInFromLeft = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

// Styled components
const HeroContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 110px;
  height: 76vh;
  color: black;
  background-color: #e0ffff;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 20px;
    height: 95vh;
  }
`;

const HeroText = styled.div`
  max-width: 600px;
  color: black;
  animation: ${slideInFromLeft} 1s ease-in-out;

  h2 {
    font-size: 2.8rem;
    margin-bottom: 20px;
  }

  p {
    font-size: 1.25rem;
    margin-bottom: 30px;
  }

  @media (max-width: 768px) {
    h1 {
      font-size: 2rem;
    }

    p {
      font-size: 1rem;
    }
  }
`;

const HeroImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 350px;
  width: 100%;
  height: 55vh;
  position: relative;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  animation: ${slideInFromRight} 1s ease-in-out;

  @media (max-width: 768px) {
    max-width: 400px;
    margin-top: 20px;
  }
`;

const Slide = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: absolute;
  transition: opacity 0.5s ease-in-out;
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0)};
`;

const SlideImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const CTAButton = styled.span`
  display: inline-block;
  padding: 15px 30px;
  background-color: #4aa7b0;
  color: #e0ffff;
  font-size: 1.25rem;
  border-radius: 5px;
  text-decoration: none;
  transition: background-color 0.3s;

  &:hover {
    background-color: #006d77;
    color: #f3e6dd;
    text-decoration: none;
  }
`;

const HeroSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const slides = [
    PhoneCase,
    KeyChains,
    Frame,
    HangingFrame,
    ClockFrame,
    Pillows,
  ];

  const handleNextSlide = useCallback(() => {
    setActiveIndex((prevIndex) => (prevIndex + 1) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    const interval = setTimeout(handleNextSlide, 3000);
    return () => clearTimeout(interval);
  }, [activeIndex, handleNextSlide]);

  return (
    <HeroContainer>
      {/* Left Side */}
      <HeroText>
        <h1>A Complete Signage & Branding Solutions</h1>
        <p>
          Get your designs printed with the best quality and fastest turnaround
          time. Perfect for business cards, flyers, posters, and more.
        </p>
        <Link to="/services">
          <CTAButton>Explore Our Services</CTAButton>
        </Link>
      </HeroText>

      {/* Right Side */}
      <HeroImage>
        {slides.map((slide, index) => {
          const isActive = index === activeIndex;
          return (
            <Slide key={index} $isActive={isActive}>
              <SlideImage src={slide} alt={`Slide ${index + 1}`} />
            </Slide>
          );
        })}
      </HeroImage>
    </HeroContainer>
  );
};

export default HeroSection;
